import BillingsTaxiTaxiEditParams, {
  BillingsTaxiTaxiEditParamsContribution,
  BillingsTaxiTaxiEditParamsContributionBonus,
  BillingsTaxiTaxiEditParamsContributionPenalty,
  BillingsTaxiTaxiEditParamsContributionType,
  BillingsTaxiTaxiEditParamsNode,
} from "../../../../../common/services/billings/taxi-taxi/edit/billings-taxi-taxi-edit-params";
import BillingsNode from "../../../common/types/billings-node";
import {
  BillingsTaxiTaxiContributionBonusReal,
  BillingsTaxiTaxiContributionBonusType,
} from "../../common/contributions/bonus/types/billings-taxi-taxi-contributions-bonus";
import {
  BillingsTaxiTaxiContributionPenaltyReal,
  BillingsTaxiTaxiContributionPenaltyType,
} from "../../common/contributions/penalty/types/billings-taxi-taxi-contributions-penalty";
import BillingFormData, {
  BillingFormDataContributions,
} from "../types/billing-form.data";

const createBillingNode = (
  node: BillingsNode,
): BillingsTaxiTaxiEditParamsNode => {
  return {
    uuid: node.id,
    nodeUuid: node.nodeId,
    orderUuid: node.orderId,
    lat: node.lat,
    lon: node.lon,
    distance: node.distance,
    description: node.description,
    checkoutDate: node.checkoutDate!,
    checkoutEventUuid: node.checkoutEventUuid,
    plannedDate: node.plannedDate!,
    haltingTime: node.haltingTime,
    allowCharge: node.isHighwayAllowed,
    highwayCharge: node.highwayCharge,
    plannedDistance: node.plannedDistance,
    editableCoordinates: node.isEditable,
  };
};

const createBillingNodes = (
  nodes: BillingsNode[],
): BillingsTaxiTaxiEditParamsNode[] => {
  return nodes.map(createBillingNode);
};

const createBillingBonusContribution = (
  contribution: BillingsTaxiTaxiContributionBonusReal,
): BillingsTaxiTaxiEditParamsContributionBonus => {
  switch (contribution.type) {
    case BillingsTaxiTaxiContributionBonusType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsTaxiTaxiEditParamsContributionType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment ?? "",
        distance: contribution.distance ?? 0,
        rate: contribution.rate ?? 0,
      };
    case BillingsTaxiTaxiContributionBonusType.BONUS_OTHER:
      return {
        type: BillingsTaxiTaxiEditParamsContributionType.BONUS_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
  }
};

const createBillingPenaltyContribution = (
  contribution: BillingsTaxiTaxiContributionPenaltyReal,
): BillingsTaxiTaxiEditParamsContributionPenalty => {
  switch (contribution.type) {
    case BillingsTaxiTaxiContributionPenaltyType.PENALTY_BEING_LATE:
      return {
        type: BillingsTaxiTaxiEditParamsContributionType.PENALTY_BEING_LATE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiTaxiContributionPenaltyType.PENALTY_INCOMPATIBLE_CAR:
      return {
        type: BillingsTaxiTaxiEditParamsContributionType.PENALTY_INCOMPATIBLE_CAR,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiTaxiContributionPenaltyType.PENALTY_OTHER:
      return {
        type: BillingsTaxiTaxiEditParamsContributionType.PENALTY_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiTaxiContributionPenaltyType.PENALTY_WRONG_ROUTE:
      return {
        type: BillingsTaxiTaxiEditParamsContributionType.PENALTY_WRONG_ROUTE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
  }
};

const createBillingContributions = (
  contributions: BillingFormDataContributions,
): BillingsTaxiTaxiEditParamsContribution[] => {
  const bonusContributions = contributions.bonus.filter(
    (contribution) => contribution.type !== null,
  ) as BillingsTaxiTaxiContributionBonusReal[];

  const penaltyContributions = contributions.penalty.filter(
    (contribution) => contribution.type !== null,
  ) as BillingsTaxiTaxiContributionPenaltyReal[];

  return [
    ...bonusContributions.map((contribution) =>
      createBillingBonusContribution(contribution),
    ),
    ...penaltyContributions.map((contribution) =>
      createBillingPenaltyContribution(contribution),
    ),
  ];
};

const createSaveBillingParams = (
  billingUuid: string,
  data: BillingFormData,
): BillingsTaxiTaxiEditParams => {
  return {
    billingUuid: billingUuid,
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
  };
};

const billingSaveRequestFactory = {
  createSaveBillingParams,
};

export default billingSaveRequestFactory;
